import {useCallback, useEffect, useRef, useState} from 'react'

import {
  clampDopamineWidth,
  DopamineAssistantStorageScope,
  DopamineWidthLimits,
  readDopamineStoredWidth,
  writeDopamineStoredWidth,
} from './DopamineAssistant.utils'

interface UseDopamineResizableWidthOptions {
  /** The width when the user has not chosen one; usually a CSS token mirrored. */
  defaultWidth: number
  limits: DopamineWidthLimits
  /** A drag ending within this many px of the default snaps back to it. */
  snapToDefaultWithin?: number
  storageKey: string
  storageScope: DopamineAssistantStorageScope | null
}

export interface DopamineResizeControls {
  /** The width in effect right now, user-set or default, already clamped. */
  currentWidth: number
  isResizing: boolean
  limits: DopamineWidthLimits
  /** Null while the element follows its default fluid width. */
  width: number | null
  beginDrag(startClientX: number): void
  drag(clientX: number): void
  endDrag(): void
  reset(): void
  setWidth(width: number): void
}

/**
 * Owns a user-chosen width: clamping, drag bookkeeping, and remembering the
 * width for the account. Shared by the past chats sidebar and the panel.
 */
export const useDopamineResizableWidth = ({
  defaultWidth,
  limits,
  snapToDefaultWithin = 0,
  storageKey,
  storageScope,
}: UseDopamineResizableWidthOptions): DopamineResizeControls => {
  const [storedWidth, setStoredWidth] = useState<number | null>(() =>
    readDopamineStoredWidth(storageKey, storageScope),
  )
  const [isResizing, setIsResizing] = useState(false)
  const dragStartRef = useRef<{clientX: number; width: number} | null>(null)
  // Latest width as set by the handlers, so the release handler can persist it
  // without depending on when React renders the last drag step.
  const latestWidthRef = useRef<number | null>(storedWidth)
  const {max, min} = limits

  const updateStoredWidth = useCallback((nextWidth: number | null): void => {
    latestWidthRef.current = nextWidth
    setStoredWidth(nextWidth)
  }, [])

  useEffect(() => {
    updateStoredWidth(readDopamineStoredWidth(storageKey, storageScope))
  }, [storageKey, storageScope, updateStoredWidth])

  const clamp = useCallback(
    (width: number): number => clampDopamineWidth(width, {max, min}),
    [max, min],
  )
  const width = storedWidth === null ? null : clamp(storedWidth)
  const currentWidth = width ?? defaultWidth

  const setWidth = useCallback(
    (nextWidth: number): void => {
      const clampedWidth = clamp(nextWidth)
      updateStoredWidth(clampedWidth)
      writeDopamineStoredWidth(storageKey, storageScope, clampedWidth)
    },
    [clamp, storageKey, storageScope, updateStoredWidth],
  )

  const reset = useCallback((): void => {
    updateStoredWidth(null)
    writeDopamineStoredWidth(storageKey, storageScope, null)
  }, [storageKey, storageScope, updateStoredWidth])

  const beginDrag = useCallback(
    (startClientX: number): void => {
      dragStartRef.current = {clientX: startClientX, width: currentWidth}
      setIsResizing(true)
    },
    [currentWidth],
  )

  const drag = useCallback(
    (clientX: number): void => {
      const dragStart = dragStartRef.current
      if (!dragStart) return

      // Only the state moves while dragging; storage is written once on release.
      const nextWidth = clamp(dragStart.width + (clientX - dragStart.clientX))
      const isNearDefault =
        Math.abs(nextWidth - defaultWidth) <= snapToDefaultWithin
      updateStoredWidth(isNearDefault ? null : nextWidth)
    },
    [clamp, defaultWidth, snapToDefaultWithin, updateStoredWidth],
  )

  const endDrag = useCallback((): void => {
    if (!dragStartRef.current) return

    dragStartRef.current = null
    setIsResizing(false)
    writeDopamineStoredWidth(storageKey, storageScope, latestWidthRef.current)
  }, [storageKey, storageScope])

  return {
    beginDrag,
    currentWidth,
    drag,
    endDrag,
    isResizing,
    limits,
    reset,
    setWidth,
    width,
  }
}
